import { Auth } from 'aws-amplify';
import { create } from 'axios';
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { atom, AtomEffect } from 'recoil';

import { CURRENT_ORGANIZATION_ID_KEY, CURRENT_WORKSPACE_ID_KEY, DO_NOT_SEND_HEADER, X_PLANERA_ORGANIZATION_ID, X_PLANERA_WORKSPACE_ID } from 'state_management/threadedConstants';

// api gateway (kong)
export const ACCOUNTS_LOCALHOST_BASE_URL = 'http://localhost:9000';
export const ACCOUNTS_REMOTE_BASE_URL = import.meta.env.VITE_ACCOUNTS_REMOTE_BASE_URL || import.meta.env.VITE_REMOTE_BASE_URL || 'https://kong.dev.planera.io/';
export const ACCOUNTS_BASE_URL_KEY = 'accounts_base_url';

const localStringStorageEffect: (key: string) => AtomEffect<string> =
  (key: string) =>
  ({ setSelf, onSet }) => {
    const savedValue = localStorage.getItem(key);
    if (savedValue != null) {
      setSelf(savedValue);
    }

    onSet((newValue, _, isReset) => {
      if (isReset) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, newValue);
      }
    });
  };

export const accountsBaseURLState = atom({
  key: 'accountsbaseurl',
  default: ACCOUNTS_REMOTE_BASE_URL,
  effects_UNSTABLE: [localStringStorageEffect(ACCOUNTS_BASE_URL_KEY)]
});

const ACCOUNTS_BASE_URL = () => {
  const savedBaseUrl = localStorage.getItem(ACCOUNTS_BASE_URL_KEY);
  return savedBaseUrl ? savedBaseUrl : ACCOUNTS_REMOTE_BASE_URL;
};

export const httpClient = create({
  baseURL: ACCOUNTS_BASE_URL()
});

const requestHandler = async (request: InternalAxiosRequestConfig) => {
  const jwt = (await Auth.currentSession()).getIdToken().getJwtToken();
  if (request && request.headers) {
    request.baseURL = ACCOUNTS_BASE_URL();
    // Only default the Content-Type when the caller did not set one. Overwriting an
    // explicit multipart/form-data with application/json makes axios 1.x JSON-stringify
    // FormData bodies, silently dropping file uploads.
    request.headers.set('Content-Type', 'application/json', false);
    request.headers.set('Authorization', `Bearer ${jwt}`);

    /**
     * - If a header is set to "DONT_SEND_HEADER", we remove that header (some API calls require no headers to be sent)
     * - If a header is missing, or blank:
     *   - If we have a corresponding value in SessionStorage, we set the header to this value
     *   - Otherwise we remove the header
     */

    const requestOrgId = request.headers.get(X_PLANERA_ORGANIZATION_ID);
    if (requestOrgId === DO_NOT_SEND_HEADER) {
      request.headers.delete(X_PLANERA_ORGANIZATION_ID);
    } else if (!requestOrgId) {
      const currentOrgId = sessionStorage.getItem(CURRENT_ORGANIZATION_ID_KEY);
      if (currentOrgId) {
        request.headers.set(X_PLANERA_ORGANIZATION_ID, currentOrgId);
      } else {
        request.headers.delete(X_PLANERA_ORGANIZATION_ID);
      }
    }

    const requestWorkspaceId = request.headers.get(X_PLANERA_WORKSPACE_ID);
    if (requestWorkspaceId === DO_NOT_SEND_HEADER) {
      request.headers.delete(X_PLANERA_WORKSPACE_ID);
    } else if (!requestWorkspaceId) {
      const currentWorkspaceId = sessionStorage.getItem(CURRENT_WORKSPACE_ID_KEY);
      if (currentWorkspaceId) {
        request.headers.set(X_PLANERA_WORKSPACE_ID, currentWorkspaceId);
      } else {
        request.headers.delete(X_PLANERA_WORKSPACE_ID);
      }
    }
  }
  return request;
};

const responseHandler = (response: AxiosResponse) => {
  if (response && response.status === 401) {
    localStorage.clear();
  }
  return response;
};

const errorHandler = (error: Error) => {
  return Promise.reject(error);
};

httpClient.interceptors.request.use(
  (request) => requestHandler(request),
  (error) => errorHandler(error)
);

httpClient.interceptors.response.use(
  (response) => responseHandler(response),
  (error) => errorHandler(error)
);
